import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { api, callableErrorMessage } from "../lib/api";

type Voucher = {
  code: string;
  discountPercent: number;
  expiresAt: Date;
};

export function CustomerQrCard({ voucher, themeColor }: { voucher: Voucher; themeColor: string }) {
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshAt, setRefreshAt] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await api.getCustomerQrToken();
        if (!alive) return;
        const svg = await QRCode.toString(res.data.token, {
          type: "svg",
          errorCorrectionLevel: "M",
          margin: 1,
          width: 220,
          color: { dark: "#1a280c", light: "#ffffff" }
        });
        if (!alive) return;
        setQrSvg(svg);
        const ttlMs = res.data.expiresInMinutes * 60 * 1000;
        const refresh = setTimeout(() => setRefreshAt(Date.now()), ttlMs - 30_000);
        return () => clearTimeout(refresh);
      } catch (err) {
        if (alive) setError(callableErrorMessage(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [refreshAt]);

  return (
    <section
      className="rounded-3xl p-5 text-white shadow-soft"
      style={{ background: `linear-gradient(135deg, ${themeColor}f0, ${themeColor}d0)` }}
    >
      <p className="text-xs uppercase tracking-wider text-white/80">Tu QR personal</p>
      <h3 className="mt-1 font-display text-xl font-semibold">
        {voucher.discountPercent}% de descuento listo
      </h3>
      <p className="mt-1 text-xs text-white/80">
        Muestra este QR al cajero al pagar. Valido hasta las{" "}
        {voucher.expiresAt.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}.
      </p>

      <div className="mt-4 grid place-items-center rounded-2xl bg-white p-3">
        {loading && <div className="h-[220px] w-[220px] animate-pulse rounded-xl bg-leaf-100" />}
        {error && (
          <div className="grid h-[220px] w-[220px] place-items-center p-3 text-center text-xs text-red-700">
            {error}
          </div>
        )}
        {!loading && !error && qrSvg && (
          // Safe: qrSvg comes from QRCode.toString() local lib, encoding a server-signed token
          <div
            className="grid place-items-center"
            dangerouslySetInnerHTML={{ __html: qrSvg }}
          />
        )}
      </div>

      <p className="mt-3 text-center font-mono text-xs text-white/90">{voucher.code}</p>
      <button
        onClick={() => setRefreshAt(Date.now())}
        className="mt-3 w-full rounded-xl border border-white/40 bg-white/10 py-2 text-xs font-medium text-white hover:bg-white/20"
      >
        Refrescar QR
      </button>
    </section>
  );
}
